import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Locale } from 'antd/es/locale';
import enUS from 'antd/locale/en_US';
import plPL from 'antd/locale/pl_PL';
import { normalizeLanguageCode } from '../utils/numberFormatting';

const antdLocales: Record<string, Locale> = {
  en: enUS,
  pl: plPL
};

export const syncDocumentLanguage = (language?: string): 'pl' | 'en' => {
  const normalized = normalizeLanguageCode(language);
  document.documentElement.lang = normalized;
  return normalized;
};

export const useLanguage = () => {
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState<Locale>(antdLocales[normalizeLanguageCode(i18n.language)]);

  useEffect(() => {
    const language = syncDocumentLanguage(i18n.language);
    setLocale(antdLocales[language]);
  }, [i18n.language]);

  useEffect(() => {
    // Load user's language preference from backend on mount
    const loadUserLanguage = async () => {
      try {
        const token = localStorage.getItem('token');
        // Skip API call if no token (user not logged in)
        if (!token) {
          // Try to load from localStorage if available
          const savedLanguage = localStorage.getItem('userLanguage');
          if (savedLanguage && savedLanguage !== i18n.language) {
            await i18n.changeLanguage(savedLanguage);
            setLocale(antdLocales[savedLanguage] || plPL);
          }
          return;
        }

        const response = await api.get('/settings');
        const { language } = response.data.user || response.data;
        if (language && language !== i18n.language) {
          await i18n.changeLanguage(language);
          setLocale(antdLocales[language] || plPL);
          localStorage.setItem('userLanguage', language);
        }
      } catch (error) {
        // Only log error if user is authenticated (has token)
        const token = localStorage.getItem('token');
        if (token) {
          console.error('Failed to load user language preference:', error);
        }
      }
    };

    loadUserLanguage();
  }, [i18n]);

  const changeLanguage = async (language: string) => {
    try {
      // Update in i18next
      await i18n.changeLanguage(language);
      
      // Update Ant Design locale
      setLocale(antdLocales[language] || plPL);
      
      // Save to localStorage
      localStorage.setItem('userLanguage', language);
      
      // Update in backend if user is logged in
      const token = localStorage.getItem('token');
      if (token) {
        await api.put('/settings', { language });
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  return {
    currentLanguage: i18n.language,
    changeLanguage,
    locale,
    availableLanguages: [
      { code: 'en', name: 'English' },
      { code: 'pl', name: 'Polski' }
    ]
  };
};
